import axios from "axios";
import { API_BASE } from "../config.js";
import { getAccessToken } from "../auth/apiAuth.jsx";

const api = axios.create({
  baseURL: `${API_BASE}/api/data`,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const tk = getAccessToken && getAccessToken();
  if (tk) config.headers.Authorization = `Bearer ${tk}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      let msg =
        data?.error?.message ||
        (typeof data?.error === "string" ? data.error : null) ||
        data?.detail ||
        data?.message ||
        "Unknown error";
      const e = new Error(msg);
      e.status = status;
      e.details = data;
      if (status === 401) e.message = "Your session has expired. Please sign in again.";
      return Promise.reject(e);
    }
    return Promise.reject(error);
  }
);

/**
 * Calculate On-Time In-Full (OTIF) percentage
 */
export async function calculateOTIF({ vendorNo, startDate, endDate, company } = {}) {
  const params = {};
  if (vendorNo) params.vendorNo = vendorNo;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (company) params.company = company;

  const response = await api.get("/dashboard/otif", { params });
  return response.data;
}

/**
 * Calculate total spend with vendors
 */
export async function calculateSpend({ vendorNo, startDate, endDate, company } = {}) {
  const params = {};
  if (vendorNo) params.vendorNo = vendorNo;
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (company) params.company = company;

  const response = await api.get("/dashboard/spend", { params });
  return response.data;
}

/**
 * Get vendor counts and status breakdown
 */
export async function getVendorCounts(company) {
  const params = {};
  if (company) params.company = company;
  const response = await api.get("/dashboard/vendors/count", { params });
  return response.data;
}

/**
 * Get spend trend data over time (monthly breakdown)
 */
export async function getSpendTrend(months = 12, company) {
  const params = { months };
  if (company) params.company = company;
  const response = await api.get("/dashboard/spend/trend", { params });
  return response.data;
}

/**
 * Get vendor performance summary
 */
export async function getVendorPerformanceSummary(vendorNo, { startDate, endDate, company } = {}) {
  const params = {};
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  if (company) params.company = company;

  const response = await api.get(`/dashboard/vendor-performance/${encodeURIComponent(vendorNo)}`, { params });
  return response.data;
}

/**
 * Get complete dashboard KPIs
 */
export async function getDashboardKPIs(company) {
  const params = {};
  if (company) params.company = company;
  const response = await api.get("/dashboard/kpis", { params });
  return response.data;
}

/**
 * Get top vendors by spend with performance metrics
 */
export async function getTopVendors(limit = 10) {
  const spend = await calculateSpend();
  const topVendorNos = spend.topVendors.slice(0, limit).map((v) => v.vendorNo);

  const vendorPerformance = await Promise.all(
    topVendorNos.map(async (vendorNo) => {
      const performance = await getVendorPerformanceSummary(vendorNo);
      return {
        ...performance,
        spend: spend.spendByVendor[vendorNo] || 0,
      };
    })
  );

  return vendorPerformance;
}
